import { useEffect, useState } from "react";
import Skyline from "./Skyline";

type Lang = "ru" | "en";

/** Иконки карточек — язык-независимы, идут в том же порядке, что и `cards`. */
const ICONS = [
  <path
    key="bridge"
    d="M2 30 H46 M8 30 V18 M40 30 V18 M8 18 C16 6 32 6 40 18 M14 30 V24 M34 30 V24 M24 30 V14"
    fill="none"
  />,
  <g key="sun">
    <circle cx="24" cy="22" r="9" fill="none" />
    <path
      d="M24 4 V9 M24 35 V40 M6 22 H11 M37 22 H42 M11 9 L14.5 12.5 M37 35 L33.5 31.5 M37 9 L33.5 12.5 M11 35 L14.5 31.5"
      fill="none"
    />
  </g>,
  <path
    key="quay"
    d="M3 34 H45 M3 26 H45 M9 26 V34 M17 26 V34 M25 26 V34 M33 26 V34 M41 26 V34 M6 26 C6 18 14 18 14 26 M20 26 C20 18 28 18 28 26 M34 26 C34 18 42 18 42 26"
    fill="none"
  />,
];

const M = {
  ru: {
    htmlLang: "ru",
    navEyebrow: "Санкт-Петербург",
    heroEyebrow: "Город · Нева · Свет",
    title1: "Санкт-",
    title2: "Петербург",
    heroSub:
      "Новый портал о городе. Пока — только небо белой ночи и тишина перед открытием.",
    cta: "Скоро",
    sectionTitle: "Характер города",
    cards: [
      {
        title: "Разводные мосты",
        text: "Каждую ночь Нева размыкает город надвое — и снова сводит к рассвету.",
      },
      {
        title: "Белые ночи",
        text: "С июня солнце почти не заходит — небо держит перламутровый свет до утра.",
      },
      {
        title: "Гранитные набережные",
        text: "Полированный камень, чугунные решётки, линия воды — фирменный профиль города.",
      },
    ],
    soonEyebrow: "Открытие готовится",
    quote: "«Здесь будет город заложён…»",
    soonText:
      "Мы собираем портал о Санкт-Петербурге — его улицах, воде и свете. Возвращайтесь: скоро здесь появится нечто большее.",
    footer: "© 2026 · Санкт-Петербург, Россия",
  },
  en: {
    htmlLang: "en",
    navEyebrow: "Saint Petersburg",
    heroEyebrow: "City · Neva · Light",
    title1: "Saint",
    title2: "Petersburg",
    heroSub:
      "A new portal about the city. For now — only the white-nights sky and the quiet before the opening.",
    cta: "Soon",
    sectionTitle: "Character of the city",
    cards: [
      {
        title: "Drawbridges",
        text: "Each night the Neva splits the city in two — and joins it back by dawn.",
      },
      {
        title: "White Nights",
        text: "From June the sun barely sets — the sky holds its pearl light until morning.",
      },
      {
        title: "Granite embankments",
        text: "Polished stone, cast-iron railings, the waterline — the city's signature profile.",
      },
    ],
    soonEyebrow: "Opening soon",
    quote: "“Here a city shall be founded…”",
    soonText:
      "We're building a portal about Saint Petersburg — its streets, water and light. Come back: something more will appear here soon.",
    footer: "© 2026 · Saint Petersburg, Russia",
  },
} satisfies Record<Lang, Record<string, unknown>>;

function detectLang(): Lang {
  const stored = localStorage.getItem("lang");
  if (stored === "ru" || stored === "en") return stored;
  return navigator.language?.toLowerCase().startsWith("en") ? "en" : "ru";
}

export default function App() {
  const [lang, setLang] = useState<Lang>(detectLang);
  const t = M[lang];

  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    localStorage.setItem("lang", lang);
  }, [lang, t.htmlLang]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink">
      {/* ───────── HERO ───────── */}
      <header className="sky grain relative isolate flex min-h-[100svh] flex-col">
        {/* верхняя строка */}
        <nav className="rise mx-auto flex w-full max-w-6xl items-center justify-between px-6 pt-7 [animation-delay:0.05s] sm:px-10">
          <span className="font-display text-2xl tracking-wide text-ink">
            spb<span className="text-gold">.</span>one
          </span>
          <LangToggle lang={lang} onChange={setLang} />
        </nav>

        {/* центр героя */}
        <div className="relative z-10 mx-auto flex w-full max-w-4xl grow flex-col items-center justify-center px-6 pb-40 text-center sm:pb-48">
          <p className="rise font-sans text-xs uppercase tracking-[0.5em] text-ink/55 [animation-delay:0.15s] sm:text-sm">
            {t.heroEyebrow}
          </p>

          <h1 className="rise mt-6 font-display text-[clamp(3.2rem,13vw,8.5rem)] font-medium leading-[0.92] tracking-tight text-ink [animation-delay:0.3s]">
            {t.title1}
            <span className="block italic text-ink/90">{t.title2}</span>
          </h1>

          <p className="rise mt-7 max-w-md font-sans text-base leading-relaxed text-ink/70 [animation-delay:0.5s] sm:text-lg">
            {t.heroSub}
          </p>

          <div className="rise mt-10 [animation-delay:0.7s]">
            <button
              type="button"
              className="group relative inline-flex items-center gap-3 rounded-full border border-gold/70 bg-paper/30 px-8 py-3.5 font-sans text-sm uppercase tracking-[0.25em] text-ink backdrop-blur-sm transition-all duration-500 hover:border-gold hover:bg-gold/10 hover:tracking-[0.3em]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gold transition-transform duration-500 group-hover:scale-150" />
              {t.cta}
            </button>
          </div>
        </div>

        {/* силуэт города */}
        <Skyline className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[34vh] min-h-[220px] w-full text-ink" />
      </header>

      {/* ───────── АТМОСФЕРА ───────── */}
      <section className="relative mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-32">
        <div className="mb-14 flex items-end justify-between gap-6">
          <h2 className="font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            {t.sectionTitle}
          </h2>
          <span className="hidden h-px grow translate-y-[-0.6rem] bg-ink/15 sm:block" />
          <span className="shrink-0 font-sans text-xs uppercase tracking-[0.3em] text-gold">
            01 — 03
          </span>
        </div>

        <div className="grid gap-px overflow-hidden rounded-2xl bg-ink/10 sm:grid-cols-3">
          {t.cards.map((c, i) => (
            <article
              key={c.title}
              className="group flex flex-col gap-5 bg-paper p-8 transition-colors duration-500 hover:bg-peach/30 sm:p-10"
            >
              <svg
                viewBox="0 0 48 44"
                className="h-12 w-12 stroke-gold stroke-[1.4] transition-transform duration-500 group-hover:scale-110 [stroke-linecap:round] [stroke-linejoin:round]"
                aria-hidden
              >
                {ICONS[i]}
              </svg>
              <h3 className="font-display text-2xl font-semibold text-ink">
                {c.title}
              </h3>
              <p className="font-sans text-[0.95rem] leading-relaxed text-ink-soft/80">
                {c.text}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* ───────── СКОРО ───────── */}
      <section className="relative isolate overflow-hidden bg-ink text-paper">
        <div className="grain absolute inset-0 opacity-100" />
        {/* мерцающие огни набережной */}
        <div className="pointer-events-none absolute inset-x-0 top-10 z-0 flex justify-center gap-6 sm:gap-10">
          {Array.from({ length: 11 }).map((_, i) => (
            <span
              key={i}
              className="h-1 w-1 rounded-full bg-gold-soft"
              style={{
                animation: `twinkle ${2.4 + (i % 4) * 0.5}s ease-in-out infinite`,
                animationDelay: `${(i % 5) * 0.3}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 mx-auto max-w-3xl px-6 py-28 text-center sm:py-36">
          <p className="font-sans text-xs uppercase tracking-[0.5em] text-gold-soft/80">
            {t.soonEyebrow}
          </p>
          <p className="mt-8 font-display text-3xl font-light italic leading-snug text-paper sm:text-5xl">
            {t.quote}
          </p>
          <p className="mt-8 font-sans text-sm leading-relaxed text-paper/60">
            {t.soonText}
          </p>
        </div>
      </section>

      {/* ───────── ФУТЕР ───────── */}
      <footer className="border-t border-ink/10 bg-paper">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 sm:flex-row sm:px-10">
          <span className="font-display text-lg text-ink">
            spb<span className="text-gold">.</span>one
          </span>
          <span className="font-sans text-xs tracking-wide text-ink/45">
            {t.footer}
          </span>
        </div>
      </footer>
    </div>
  );
}

/** Сегментный переключатель языка RU / EN. */
function LangToggle({
  lang,
  onChange,
}: {
  lang: Lang;
  onChange: (l: Lang) => void;
}) {
  return (
    <div
      className="flex items-center gap-1 rounded-full border border-ink/15 bg-paper/30 p-0.5 backdrop-blur-sm"
      role="group"
      aria-label="Language / Язык"
    >
      {(["ru", "en"] as const).map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={lang === l}
          onClick={() => onChange(l)}
          className={`rounded-full px-3 py-1 font-sans text-[0.7rem] uppercase tracking-[0.2em] transition-colors duration-300 ${
            lang === l
              ? "bg-ink text-paper"
              : "text-ink/55 hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
